import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  MarkerType,
  useNodesState,
  useEdgesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { X, Check, GitBranch, CircleHelp } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import './RemanejamentoEquipamentoFlow.css';

const API_BASE_URL = getApiBaseUrl();
const REM_PROCESS_API = `${API_BASE_URL}/api/config/vc-sys-process/remanejamento-estoque`;

let remUid = 0;
function nextRemId(prefix) {
  remUid += 1;
  return `rem_${prefix}_${remUid}`;
}

const REM_NODE_WIDTH = 180;
const REM_COLUMN_GAP = 56;
const REM_FLOW_STRIDE = REM_NODE_WIDTH + REM_COLUMN_GAP;
const REM_MAIN_ROW_Y = 48;
const REM_DECISION_NODE_WIDTH = 160;
/** Caracteres que cabem no losango; acima disso o texto sobe */
const REM_DECISION_INLINE_CHAR_LIMIT = 22;
const REM_NAO_BRANCH_Y = REM_MAIN_ROW_Y + 156;
const REM_ABERTURA_CFG_ID = 'rem_abertura_cfg';
const REM_ABERTURA_CFG_NODE_WIDTH = 460;
const REM_ABERTURA_CFG_Y = REM_MAIN_ROW_Y + 188;
/** Margem esquerda mínima (evita corte do painel verde e botão excluir) */
const REM_LAYOUT_PAD_X = 80;

const REM_CFG_OPENING_IDS = [
  'rem_cfg_operacao',
  'rem_cfg_condicao',
  'rem_cfg_vendedor',
  'rem_cfg_transportadora',
  'rem_cfg_classificacao',
];

const REM_CFG_FIELD_META = {
  rem_cfg_operacao: { label: 'Operação', max: 2 },
  rem_cfg_condicao: { label: 'Condição', max: 3 },
  rem_cfg_vendedor: { label: 'Vendedor', max: 4 },
  rem_cfg_transportadora: { label: 'Transportadora', max: 4 },
  rem_cfg_classificacao: { label: 'Classificação', max: 4 },
};

const remEdgeLabelStyle = { fill: '#475569', fontWeight: 600, fontSize: 11, fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif" };

function buildEmptyRemCfg() {
  return Object.fromEntries(REM_CFG_OPENING_IDS.map((id) => [id, '']));
}

function normalizeRemCod(raw) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 2);
}

function normalizeRemCfgDigits(raw, maxLen) {
  return String(raw ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, maxLen);
}

function isConfiguredCod(cod) {
  return normalizeRemCod(cod).length === 2;
}

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function graphToSavePayload(graph, opening) {
  return {
    opening,
    nodes: graph.nodes.map((n) => ({
      id: n.id,
      kind: n.kind,
      label: n.label ?? '',
      cod: n.cod ?? '',
      isFinalStatus: Boolean(n.isFinalStatus),
    })),
    edges: graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      sourceHandle: e.sourceHandle || 'main',
      target: e.target,
      ...(e.targetHandle ? { targetHandle: e.targetHandle } : {}),
      ...(e.label ? { label: e.label } : {}),
    })),
  };
}

function createInitialGraph() {
  const rootId = nextRemId('step');
  return {
    nodes: [{ id: rootId, kind: 'step', label: '', cod: '' }],
    edges: [],
  };
}

function findRootId(nodes, edges) {
  const flowTargets = new Set(
    edges.filter((e) => e.source !== REM_ABERTURA_CFG_ID).map((e) => e.target)
  );
  const step = nodes.find((n) => n.kind === 'step' && !flowTargets.has(n.id));
  return step?.id ?? nodes.find((n) => n.kind === 'step')?.id ?? null;
}

function getMainSuccessor(nodeId, edges) {
  const out = edges.filter(
    (e) =>
      e.source === nodeId &&
      e.sourceHandle !== 'nao' &&
      e.sourceHandle !== 'cfg' &&
      (e.sourceHandle === 'main' || e.sourceHandle === 'sim')
  );
  return (out.find((e) => e.sourceHandle === 'main') ?? out[0])?.target ?? null;
}

function isNaoBranchNode(nodeId, edges) {
  return edges.some((e) => e.target === nodeId && e.sourceHandle === 'nao');
}

function layoutChain(nodes, edges, startId, startCol, rowY, positions, visited) {
  let col = startCol;
  let nodeId = startId;

  while (nodeId && !visited.has(nodeId)) {
    visited.add(nodeId);
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) break;

    if (node.kind === 'decision') {
      positions[nodeId] = {
        x: col * REM_FLOW_STRIDE + (REM_NODE_WIDTH - REM_DECISION_NODE_WIDTH) / 2,
        y: rowY + 6,
      };
      const naoEdge = edges.find((e) => e.source === nodeId && e.sourceHandle === 'nao');
      if (naoEdge?.target) {
        layoutChain(nodes, edges, naoEdge.target, col, REM_NAO_BRANCH_Y, positions, visited);
      }
    } else {
      positions[nodeId] = { x: col * REM_FLOW_STRIDE, y: rowY };
    }

    const nextId = getMainSuccessor(nodeId, edges);
    if (!nextId) break;
    col += 1;
    nodeId = nextId;
  }

  return col;
}

function computeLayout(nodes, edges) {
  const positions = {};
  const rootId = findRootId(nodes, edges);
  if (rootId) {
    layoutChain(nodes, edges, rootId, 0, REM_MAIN_ROW_Y, positions, new Set());
  }

  const rootX = rootId && positions[rootId] ? positions[rootId].x : 0;
  positions[REM_ABERTURA_CFG_ID] = {
    x: rootX + (REM_NODE_WIDTH - REM_ABERTURA_CFG_NODE_WIDTH) / 2,
    y: REM_ABERTURA_CFG_Y,
  };

  const xs = Object.values(positions).map((p) => p.x);
  const minX = xs.length ? Math.min(...xs) : 0;
  if (minX < REM_LAYOUT_PAD_X) {
    const shift = REM_LAYOUT_PAD_X - minX;
    Object.keys(positions).forEach((id) => {
      positions[id] = { ...positions[id], x: positions[id].x + shift };
    });
  }

  return positions;
}

/** Mesmos parâmetros do fluxo de suprimentos (CustomersProcessosView) */
const REM_FIT_VIEW_OPTS = { padding: 0.2, maxZoom: 0.82, minZoom: 0.28, duration: 0 };

function collectMainChainFrom(startId, edges) {
  const ids = [];
  let current = startId;
  const visited = new Set();
  while (current && !visited.has(current)) {
    visited.add(current);
    ids.push(current);
    current = getMainSuccessor(current, edges);
  }
  return ids;
}

/** Último status (step) na cadeia principal do fluxo */
function findLastMainChainStepId(nodes, edges) {
  const rootId = findRootId(nodes, edges);
  if (!rootId) return null;
  const chain = collectMainChainFrom(rootId, edges);
  let lastStepId = null;
  for (const id of chain) {
    const node = nodes.find((n) => n.id === id);
    if (node?.kind === 'step') lastStepId = id;
  }
  return lastStepId;
}

function removeNodeFromGraph(graph, nodeId, rootId) {
  if (!nodeId || nodeId === rootId) return graph;

  const { nodes, edges } = graph;
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return graph;

  const idsToRemove = new Set([nodeId]);

  if (node.kind === 'decision') {
    const naoEdge = edges.find((e) => e.source === nodeId && e.sourceHandle === 'nao');
    if (naoEdge?.target) {
      collectMainChainFrom(naoEdge.target, edges).forEach((id) => idsToRemove.add(id));
    }
  }

  const inEdges = edges.filter((e) => e.target === nodeId);
  const outEdge = edges.find(
    (e) =>
      e.source === nodeId &&
      e.sourceHandle !== 'nao' &&
      e.sourceHandle !== 'cfg' &&
      (e.sourceHandle === 'main' || e.sourceHandle === 'sim')
  );
  const outTarget = outEdge?.target && !idsToRemove.has(outEdge.target) ? outEdge.target : null;

  const newEdges = edges.filter((e) => !idsToRemove.has(e.source) && !idsToRemove.has(e.target));

  if (outTarget) {
    inEdges.forEach((inE) => {
      if (idsToRemove.has(inE.source)) return;
      const targetHandle = inE.targetHandle || (isNaoBranchNode(outTarget, newEdges) ? 'top' : undefined);
      newEdges.push({
        id: `e-bridge-${inE.source}-${outTarget}-${nextRemId('br')}`,
        source: inE.source,
        sourceHandle: inE.sourceHandle || 'main',
        target: outTarget,
        ...(targetHandle ? { targetHandle } : {}),
        ...(inE.label ? { label: inE.label } : {}),
      });
    });
  }

  return {
    nodes: nodes.filter((n) => !idsToRemove.has(n.id)),
    edges: newEdges,
  };
}

function insertFromConnection(graph, sourceId, sourceHandle, kind) {
  const { nodes, edges } = graph;
  const prevEdge = edges.find((e) => e.source === sourceId && e.sourceHandle === sourceHandle);
  const withoutPrev = edges.filter((e) => !(e.source === sourceId && e.sourceHandle === sourceHandle));

  if (kind === 'step') {
    const newId = nextRemId('step');
    const newNode = { id: newId, kind: 'step', label: '', cod: '' };
    const newEdges = [
      ...withoutPrev,
      {
        id: `e-${sourceId}-${newId}`,
        source: sourceId,
        sourceHandle,
        target: newId,
        ...(sourceHandle === 'nao' ? { targetHandle: 'top' } : {}),
      },
    ];
    if (prevEdge) {
      newEdges.push({
        id: `e-${newId}-${prevEdge.target}`,
        source: newId,
        sourceHandle: 'main',
        target: prevEdge.target,
        ...(prevEdge.targetHandle ? { targetHandle: prevEdge.targetHandle } : {}),
      });
    }
    return { nodes: [...nodes, newNode], edges: newEdges };
  }

  const decId = nextRemId('dec');
  const naoId = nextRemId('step');
  const decNode = { id: decId, kind: 'decision', label: '' };
  const naoNode = { id: naoId, kind: 'step', label: '', cod: '' };
  const newEdges = [
    ...withoutPrev,
    {
      id: `e-${sourceId}-${decId}`,
      source: sourceId,
      sourceHandle,
      target: decId,
      targetHandle: 'in',
    },
    {
      id: `e-${decId}-${naoId}`,
      source: decId,
      sourceHandle: 'nao',
      target: naoId,
      targetHandle: 'top',
      label: 'Não',
    },
  ];
  if (prevEdge) {
    newEdges.push({
      id: `e-${decId}-sim-${prevEdge.target}`,
      source: decId,
      sourceHandle: 'sim',
      target: prevEdge.target,
      label: 'Sim',
      ...(prevEdge.targetHandle ? { targetHandle: prevEdge.targetHandle } : {}),
    });
  }
  return { nodes: [...nodes, decNode, naoNode], edges: newEdges };
}

function buildFlowEdges(graph) {
  const { nodes, edges } = graph;
  const edgeStyle = {
    animated: false,
    interactionWidth: 0,
    style: { stroke: '#94a3b8', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8', width: 16, height: 16 },
  };
  const rootId = findRootId(nodes, edges);
  const rfEdges = [];

  if (rootId) {
    rfEdges.push({
      id: 'e-rem_first-cfg',
      type: 'straight',
      source: rootId,
      sourceHandle: 'cfg',
      target: REM_ABERTURA_CFG_ID,
      targetHandle: 'top',
      animated: false,
      interactionWidth: 0,
      style: { stroke: '#111827', strokeWidth: 1.7, strokeDasharray: '6 4' },
    });
  }

  edges.forEach((e) => {
    rfEdges.push({
      ...e,
      type: e.sourceHandle === 'nao' ? 'straight' : undefined,
      labelStyle: e.label ? remEdgeLabelStyle : undefined,
      labelBgStyle: e.label ? { fill: '#fff', fillOpacity: 0.92 } : undefined,
      ...edgeStyle,
    });
  });

  return rfEdges;
}

const RemDropPicker = memo(function RemDropPicker({ x, y, allowDecision, onPick, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current?.contains(e.target)) return;
      onClose();
    };
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDoc, true);
      document.addEventListener('touchstart', onDoc, true);
    }, 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc, true);
      document.removeEventListener('touchstart', onDoc, true);
    };
  }, [onClose]);

  return createPortal(
    <>
      <button
        type="button"
        className="cp-rem-drop-picker-backdrop"
        aria-label="Fechar menu de adicionar"
        tabIndex={-1}
        onMouseDown={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        ref={ref}
        className="cp-rem-drop-picker"
        style={{ left: x, top: y }}
        role="dialog"
        aria-label="Tipo do novo elemento"
      >
      <p className="cp-rem-drop-picker__title">Adicionar</p>
      <button type="button" className="cp-rem-drop-picker__item" onClick={() => onPick('step')}>
        <GitBranch size={14} strokeWidth={2} aria-hidden />
        <span>Novo status</span>
      </button>
      {allowDecision && (
        <button type="button" className="cp-rem-drop-picker__item" onClick={() => onPick('decision')}>
          <CircleHelp size={14} strokeWidth={2} aria-hidden />
          <span>Pergunta</span>
        </button>
      )}
      </div>
    </>,
    document.body
  );
});

const RemStepNode = memo(function RemStepNode({ data }) {
  const {
    label,
    draft,
    active,
    stepId,
    isFirst,
    isNaoBranch,
    canDelete,
    showFinalToggle,
    isFinalStatus,
    onLabel,
    onDraft,
    onDelete,
    onToggleFinalStatus,
  } = data;
  const stopFlow = (e) => e.stopPropagation();

  return (
    <div className={`cp-rem-node ${active ? 'cp-rem-node--on' : 'cp-rem-node--off'}`}>
      {canDelete && (
        <button
          type="button"
          className="cp-rem-node__delete nodrag nopan nowheel"
          title="Excluir status"
          aria-label="Excluir status"
          onMouseDown={stopFlow}
          onPointerDown={stopFlow}
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.(stepId);
          }}
        >
          <X size={11} strokeWidth={2.5} aria-hidden />
        </button>
      )}
      {isNaoBranch && (
        <Handle
          type="target"
          position={Position.Top}
          id="top"
          isConnectable={false}
          style={{ background: '#94a3b8' }}
        />
      )}
      {!isFirst && (
        <Handle type="target" position={Position.Left} isConnectable={false} style={{ background: '#94a3b8' }} />
      )}
      <div className="cp-rem-node__head-row">
        <input
          type="text"
          className="cp-rem-node__label nodrag nopan nowheel"
          placeholder={isFirst ? 'Nome do status' : 'Nome da etapa'}
          maxLength={48}
          autoComplete="off"
          aria-label="Nome da etapa"
          value={label}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onChange={(e) => onLabel(stepId, e.target.value)}
        />
        <div className="cp-rem-node__actions">
          {showFinalToggle ? (
            <button
              type="button"
              className={`cp-rem-node__final cp-rem-tooltip-host nodrag nopan nowheel ${isFinalStatus ? 'cp-rem-node__final--on' : ''}`}
              data-tooltip="Status final"
              aria-label="Status final"
              aria-pressed={isFinalStatus}
              onMouseDown={stopFlow}
              onPointerDown={stopFlow}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFinalStatus?.(stepId);
              }}
            >
              <Check size={11} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
          <span
            className={`cp-rem-node__status ${active ? 'cp-rem-node__status--ok' : 'cp-rem-node__status--off'}`}
            title={active ? 'Configurado' : 'Não configurado'}
            aria-label={active ? 'Etapa configurada' : 'Etapa não configurada'}
          >
            {active ? <Check size={11} strokeWidth={2.5} aria-hidden /> : <X size={11} strokeWidth={2.5} aria-hidden />}
          </span>
        </div>
      </div>
      <input
        type="text"
        className="cp-rem-node__input nodrag nopan nowheel"
        maxLength={2}
        autoComplete="off"
        aria-label={`Código de status — ${label || 'etapa'}`}
        value={draft}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onChange={(e) => onDraft(stepId, normalizeRemCod(e.target.value))}
      />
      {isFirst && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="cfg"
          isConnectable={false}
          style={{ background: '#94a3b8' }}
        />
      )}
      <Handle
        type="source"
        position={Position.Right}
        id="main"
        className="cp-rem-handle cp-rem-handle--main"
        style={{ background: '#7c3aed' }}
      />
    </div>
  );
});

const RemDecisionNode = memo(function RemDecisionNode({ data }) {
  const { label, stepId, onLabel, onDelete } = data;
  const expanded = label.length >= REM_DECISION_INLINE_CHAR_LIMIT;
  const stopFlow = (e) => e.stopPropagation();

  return (
    <div className={`cp-rem-decision nodrag nopan nowheel ${expanded ? 'cp-rem-decision--expanded' : ''}`}>
      <button
        type="button"
        className="cp-rem-decision__delete nodrag nopan nowheel"
        title="Excluir pergunta"
        aria-label="Excluir pergunta"
        onMouseDown={stopFlow}
        onPointerDown={stopFlow}
        onClick={(e) => {
          e.stopPropagation();
          onDelete?.(stepId);
        }}
      >
        <X size={11} strokeWidth={2.5} aria-hidden />
      </button>
      <Handle type="target" position={Position.Left} id="in" isConnectable={false} style={{ background: '#64748b' }} />
      {expanded ? (
        <textarea
          className="cp-rem-decision__input-above nodrag nopan nowheel"
          placeholder="Pergunta?"
          maxLength={48}
          rows={Math.min(4, Math.max(2, Math.ceil(label.length / 28)))}
          autoComplete="off"
          aria-label="Texto da pergunta"
          value={label}
          onMouseDown={stopFlow}
          onPointerDown={stopFlow}
          onChange={(e) => onLabel(stepId, e.target.value)}
        />
      ) : (
        <input
          type="text"
          className="cp-rem-decision__input nodrag nopan nowheel"
          placeholder="Pergunta?"
          maxLength={48}
          autoComplete="off"
          aria-label="Texto da pergunta"
          value={label}
          onMouseDown={stopFlow}
          onPointerDown={stopFlow}
          onChange={(e) => onLabel(stepId, e.target.value)}
        />
      )}
      <div className="cp-rem-decision__shape" aria-hidden />
      <Handle
        type="source"
        position={Position.Right}
        id="sim"
        className="cp-rem-handle cp-rem-handle--sim"
        style={{ top: '38%', background: '#64748b' }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="nao"
        className="cp-rem-handle cp-rem-handle--nao"
        style={{ background: '#64748b' }}
      />
    </div>
  );
});

const RemOpeningConfigNode = memo(function RemOpeningConfigNode({ data }) {
  const { fields, onDraft, invalidFieldIds = [] } = data;
  const invalidSet = useMemo(() => new Set(invalidFieldIds), [invalidFieldIds]);
  return (
    <div className="cp-rem-config-node nodrag nopan nowheel">
      <Handle type="target" position={Position.Top} id="top" isConnectable={false} style={{ background: '#0f766e' }} />
      <div className="cp-rem-config-node__title">Configuração da abertura do remanejamento</div>
      <div className="cp-rem-config-node__grid">
        {REM_CFG_OPENING_IDS.map((id) => {
          const meta = REM_CFG_FIELD_META[id];
          const isInvalid = invalidSet.has(id);
          return (
            <label key={id} className={`cp-rem-config-node__field ${isInvalid ? 'cp-rem-config-node__field--invalid' : ''}`}>
              <span>{meta.label}</span>
              <input
                type="text"
                className={`cp-rem-config-node__input nodrag nopan nowheel ${isInvalid ? 'cp-rem-config-node__input--invalid' : ''}`}
                maxLength={meta.max}
                autoComplete="off"
                value={fields[id] ?? ''}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onChange={(e) => onDraft(id, normalizeRemCfgDigits(e.target.value, meta.max))}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
});

const remNodeTypes = {
  remStep: RemStepNode,
  remDecision: RemDecisionNode,
  remOpeningConfig: RemOpeningConfigNode,
};

export default function RemanejamentoEquipamentoFlow() {
  const [graph, setGraph] = useState(createInitialGraph);
  const [cfgFields, setCfgFields] = useState(buildEmptyRemCfg);
  const [dropPicker, setDropPicker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [cfgInvalidIds, setCfgInvalidIds] = useState([]);

  const connectStartRef = useRef(null);
  const connectionMadeRef = useRef(false);
  const remFlowRef = useRef(null);
  const flowWrapRef = useRef(null);

  const rootId = useMemo(() => findRootId(graph.nodes, graph.edges), [graph]);
  const lastStepId = useMemo(
    () => findLastMainChainStepId(graph.nodes, graph.edges),
    [graph.nodes, graph.edges]
  );

  useEffect(() => {
    if (!lastStepId) return;
    setGraph((prev) => {
      const marked = prev.nodes.find((n) => n.isFinalStatus);
      if (!marked || marked.id === lastStepId) return prev;
      return {
        ...prev,
        nodes: prev.nodes.map((n) => ({ ...n, isFinalStatus: false })),
      };
    });
  }, [lastStepId]);

  const onRemLabel = useCallback((nodeId, label) => {
    setGraph((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) => (n.id === nodeId ? { ...n, label } : n)),
    }));
  }, []);

  const onRemDraft = useCallback((nodeId, cod) => {
    setGraph((prev) => ({
      ...prev,
      nodes: prev.nodes.map((n) => (n.id === nodeId ? { ...n, cod } : n)),
    }));
  }, []);

  const onToggleFinalStatus = useCallback((nodeId) => {
    let nextVal = false;
    setGraph((prev) => {
      const target = prev.nodes.find((n) => n.id === nodeId);
      nextVal = !Boolean(target?.isFinalStatus);
      return {
        ...prev,
        nodes: prev.nodes.map((n) => {
          if (n.kind === 'decision') return { ...n, isFinalStatus: false };
          if (n.id === nodeId) return { ...n, isFinalStatus: nextVal };
          return { ...n, isFinalStatus: false };
        }),
      };
    });
    setSaveMsg(null);

    fetch(`${REM_PROCESS_API}/final-status`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify({ nodeKey: nodeId, isFinalStatus: nextVal }),
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (res.status === 409) return;
        if (!res.ok || json.success === false) {
          throw new Error(json.message || `Erro ${res.status}`);
        }
      })
      .catch((e) => {
        setSaveMsg({ type: 'err', text: e.message || 'Erro ao salvar status final' });
      });
  }, []);

  const onCfgDraft = useCallback((id, v) => {
    setCfgFields((prev) => ({ ...prev, [id]: v }));
    setSaveMsg(null);
    setCfgInvalidIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const loadProcess = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(REM_PROCESS_API, { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Erro ${res.status}`);
      }
      const data = json.data;
      if (data && typeof data === 'object') {
        const opening = data.opening && typeof data.opening === 'object' ? data.opening : {};
        const nextCfg = buildEmptyRemCfg();
        REM_CFG_OPENING_IDS.forEach((id) => {
          const v = opening[id];
          nextCfg[id] =
            v != null && String(v).trim() !== ''
              ? normalizeRemCfgDigits(v, REM_CFG_FIELD_META[id].max)
              : '';
        });
        setCfgFields(nextCfg);
        const nodes = Array.isArray(data.nodes) && data.nodes.length > 0
          ? data.nodes.map((n) => ({
              ...n,
              isFinalStatus: Boolean(n.isFinalStatus),
            }))
          : createInitialGraph().nodes;
        const edges = Array.isArray(data.edges) ? data.edges : [];
        setGraph({ nodes, edges });
      } else {
        setGraph(createInitialGraph());
        setCfgFields(buildEmptyRemCfg());
      }
      setCfgInvalidIds([]);
    } catch (e) {
      setLoadError(e.message || 'Falha ao carregar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProcess();
  }, [loadProcess]);

  const cfgMissingIds = useMemo(
    () =>
      REM_CFG_OPENING_IDS.filter((id) => {
        const v = normalizeRemCfgDigits(cfgFields[id], REM_CFG_FIELD_META[id].max);
        return v.length !== REM_CFG_FIELD_META[id].max;
      }),
    [cfgFields]
  );
  const cfgAllFilled = cfgMissingIds.length === 0;

  const stepsValid = useMemo(
    () =>
      graph.nodes
        .filter((n) => n.kind === 'step')
        .every((n) => {
          const c = normalizeRemCod(n.cod);
          return c.length === 0 || c.length === 2;
        }),
    [graph.nodes]
  );

  const saveProcess = useCallback(async () => {
    if (cfgMissingIds.length > 0) {
      setCfgInvalidIds(cfgMissingIds);
      setSaveMsg({ type: 'err', text: 'Preencha a configuração da abertura do remanejamento.' });
      return;
    }
    if (!stepsValid) {
      setSaveMsg({ type: 'err', text: 'Há status com código inválido (use 2 caracteres ou vazio).' });
      return;
    }
    const opening = {};
    REM_CFG_OPENING_IDS.forEach((id) => {
      opening[id] = normalizeRemCfgDigits(cfgFields[id], REM_CFG_FIELD_META[id].max);
    });
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(REM_PROCESS_API, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(graphToSavePayload(graph, opening)),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        throw new Error(json.message || `Erro ${res.status}`);
      }
      const data = json.data;
      if (data?.opening) {
        const nextCfg = buildEmptyRemCfg();
        REM_CFG_OPENING_IDS.forEach((id) => {
          const v = data.opening[id];
          nextCfg[id] = v != null && String(v).trim() !== '' ? normalizeRemCfgDigits(v, REM_CFG_FIELD_META[id].max) : '';
        });
        setCfgFields(nextCfg);
      }
      if (data?.nodes?.length) {
        setGraph({
          nodes: data.nodes.map((n) => ({
            ...n,
            isFinalStatus: Boolean(n.isFinalStatus),
          })),
          edges: Array.isArray(data.edges) ? data.edges : graph.edges,
        });
      }
      setSaveMsg({ type: 'ok', text: 'Fluxo salvo.' });
    } catch (e) {
      setSaveMsg({ type: 'err', text: e.message || 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  }, [graph, cfgFields, cfgMissingIds, stepsValid]);

  const onRemDelete = useCallback((nodeId) => {
    setGraph((prev) => removeNodeFromGraph(prev, nodeId, findRootId(prev.nodes, prev.edges)));
  }, []);

  const onConnectStart = useCallback(
    (_, params) => {
      connectStartRef.current = null;
      connectionMadeRef.current = false;
      if (!params.nodeId || params.handleId === 'cfg' || params.handleType === 'target') return;
      if (params.nodeId === rootId && params.handleId !== 'main') return;
      connectStartRef.current = params;
    },
    [rootId]
  );

  const onConnect = useCallback(() => {
    connectionMadeRef.current = true;
  }, []);

  const isValidConnection = useCallback(() => false, []);

  const onConnectEnd = useCallback((event) => {
    const start = connectStartRef.current;
    connectStartRef.current = null;
    if (connectionMadeRef.current || !start?.nodeId || start.handleId === 'cfg') return;

    const { clientX, clientY } = 'changedTouches' in event ? event.changedTouches[0] : event;
    setDropPicker({
      x: clientX,
      y: clientY,
      sourceId: start.nodeId,
      sourceHandle: start.handleId || 'main',
      allowDecision: start.handleId !== 'nao',
    });
  }, []);

  const onDropPick = useCallback(
    (kind) => {
      if (!dropPicker) return;
      setGraph((prev) =>
        insertFromConnection(prev, dropPicker.sourceId, dropPicker.sourceHandle, kind)
      );
      setDropPicker(null);
    },
    [dropPicker]
  );

  const layout = useMemo(() => computeLayout(graph.nodes, graph.edges), [graph]);
  const rfEdges = useMemo(() => buildFlowEdges(graph), [graph]);

  const [remNodes, setRemNodes, onRemNodesChange] = useNodesState([]);
  const [remEdgesState, setRemEdgesState, onRemEdgesChange] = useEdgesState([]);

  useEffect(() => {
    setRemEdgesState(rfEdges);
  }, [rfEdges, setRemEdgesState]);

  useEffect(() => {
    const rfNodes = graph.nodes.map((node) => {
      const pos = layout[node.id] ?? { x: 0, y: REM_MAIN_ROW_Y };
      if (node.kind === 'decision') {
        return {
          id: node.id,
          type: 'remDecision',
          position: pos,
          data: { label: node.label, stepId: node.id, onLabel: onRemLabel, onDelete: onRemDelete },
        };
      }
      return {
        id: node.id,
        type: 'remStep',
        position: pos,
        data: {
          label: node.label,
          draft: node.cod,
          active: isConfiguredCod(node.cod),
          stepId: node.id,
          isFirst: node.id === rootId,
          isNaoBranch: isNaoBranchNode(node.id, graph.edges),
          canDelete: node.id !== rootId,
          showFinalToggle: node.id === lastStepId,
          isFinalStatus: Boolean(node.isFinalStatus),
          onLabel: onRemLabel,
          onDraft: onRemDraft,
          onDelete: onRemDelete,
          onToggleFinalStatus,
        },
      };
    });

    rfNodes.push({
      id: REM_ABERTURA_CFG_ID,
      type: 'remOpeningConfig',
      position: layout[REM_ABERTURA_CFG_ID] ?? { x: 0, y: REM_ABERTURA_CFG_Y },
      data: { fields: cfgFields, onDraft: onCfgDraft, invalidFieldIds: cfgInvalidIds },
    });

    setRemNodes(rfNodes);
  }, [graph, layout, rootId, lastStepId, cfgFields, cfgInvalidIds, onRemLabel, onRemDraft, onRemDelete, onToggleFinalStatus, onCfgDraft, setRemNodes]);

  const fitRemView = useCallback(() => {
    const inst = remFlowRef.current;
    if (!inst) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const nodes = inst.getNodes();
        if (nodes.length === 0) return;
        inst.fitView({ ...REM_FIT_VIEW_OPTS, nodes });
      });
    });
  }, []);

  useEffect(() => {
    if (remNodes.length === 0) return;
    fitRemView();
  }, [graph.nodes.length, graph.edges.length, remNodes.length, fitRemView]);

  useEffect(() => {
    const el = flowWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    let t = null;
    const ro = new ResizeObserver(() => {
      if (t) window.clearTimeout(t);
      t = window.setTimeout(fitRemView, 120);
    });
    ro.observe(el);
    return () => {
      if (t) window.clearTimeout(t);
      ro.disconnect();
    };
  }, [fitRemView]);

  if (loading) {
    return (
      <div className="cp-panel cp-panel--rem">
        <p className="cp-panel-desc" style={{ margin: 0 }}>Carregando fluxo…</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="cp-panel cp-panel--rem">
        <p className="cp-msg cp-msg--err">{loadError}</p>
        <button type="button" className="cp-btn cp-btn--primary" onClick={loadProcess}>
          Tentar novamente
        </button>
      </div>
    );
  }

  const saveDisabled = saving || !cfgAllFilled || !stepsValid;

  return (
    <div className="cp-panel cp-panel--rem">
      <h2 className="cp-panel-title">Fluxo de remanejamento de equipamento</h2>
      <p className="cp-panel-desc">
        Arraste do ponto à direita do status para adicionar novos status ou perguntas no fluxo.
      </p>
      <div ref={flowWrapRef} className="cp-flow-wrap cp-flow-wrap--rem">
        <div className="cp-flow-inner cp-flow-inner--rem">
          <ReactFlow
            nodes={remNodes}
            edges={remEdgesState}
            onNodesChange={onRemNodesChange}
            onEdgesChange={onRemEdgesChange}
            nodeTypes={remNodeTypes}
            onConnectStart={onConnectStart}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            isValidConnection={isValidConnection}
            defaultEdgeOptions={{ interactionWidth: 0 }}
            connectionLineStyle={{ stroke: '#7c3aed', strokeWidth: 2, strokeDasharray: '6 4' }}
            nodesDraggable={false}
            nodesConnectable
            nodesFocusable={false}
            elementsSelectable={false}
            selectNodesOnDrag={false}
            edgesFocusable={false}
            panOnScroll
            zoomOnScroll={false}
            preventScrolling={false}
            onInit={(inst) => {
              remFlowRef.current = inst;
              fitRemView();
            }}
            minZoom={0.28}
            maxZoom={1.25}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} color="#e2e8f0" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
      </div>
      {dropPicker && (
        <RemDropPicker
          x={dropPicker.x}
          y={dropPicker.y}
          allowDecision={dropPicker.allowDecision}
          onPick={onDropPick}
          onClose={() => setDropPicker(null)}
        />
      )}
      <div className="cp-panel-footer">
        {saveMsg && (
          <p className={`cp-panel-footer__msg cp-msg ${saveMsg.type === 'err' ? 'cp-msg--err' : 'cp-msg--ok'}`}>
            {saveMsg.text}
          </p>
        )}
        <button
          type="button"
          className="cp-btn cp-btn--verve-static cp-panel-footer__btn"
          disabled={saveDisabled}
          onClick={saveProcess}
        >
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
